import { fetchAuthSession } from '@aws-amplify/auth';
import env from '../config/env';
import { BACKEND_SEMANTIC_DATABASE_API, WHISPERBI_SECURITY_API } from '../env';

const defaultHeaders = env.API_KEY ? { 'x-api-key': env.API_KEY } : {};

const SOURCE_CONNECTIONS_REST_BASE = WHISPERBI_SECURITY_API;

//Semantic Database API base URL
const BASE_URL = BACKEND_SEMANTIC_DATABASE_API;


async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    //const accessToken = tokens?.accessToken?.toString();

    if (idToken) {
      return { Authorization: `Bearer ${idToken}` };
    }
    /*
    if (accessToken) {
      return { Authorization: `Bearer ${accessToken}` };
    }
    */
  } catch (error) {
    console.error('SemanticDatabaseService.getAuthHeader: unable to fetch session', error);
  }

  return {};
}

async function fetchWithAuth(input, init = {}) {

  const authHeader = await getAuthHeader();

  const headers = {
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
    //...defaultHeaders,
    ...authHeader
  };

  // SECURITY (fix MED-06, auditoría 2026-09-07): no loguear el Cognito ID
  // token en texto plano -- el header Authorization completo se logueaba
  // sin redactar en cada request.
  console.log('Fetching %s with headers: %o', JSON.stringify(input), JSON.stringify({ ...headers, ...(headers.Authorization ? { Authorization: '***' } : {}) }));

  const response = await fetch(input, {
    ...init,
    headers,
    mode: 'cors',
    cache: 'no-cache'
  });

  
  console.log('Response from %s: %o', JSON.stringify(input), response );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${errorBody || 'Unexpected error'}`);
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text || null;
  }
}



/**
 * @typedef {Object} SemanticDatabase
 * @property {string} semantic_database_id - The unique identifier of the semantic database.
 * @property {string} account_id - The account ID associated with the semantic database.
 * @property {string} connection_id - The connection ID associated with the semantic database.
 * @property {string} name - The name of the semantic database.
 * @property {string} description - A brief description of the semantic database.
 * @property {boolean} is_deleted - Indicates if the semantic database is soft-deleted.
 * @property {string} created_at - Timestamp of when the semantic database was created.
 * @property {string} updated_at - Timestamp of when the semantic database was last updated.
 * 
 */
const SemanticDatabaseService = {

  async list(accountId, { includeDeleted = false } = {}) {

    if (!accountId) {
      throw new Error('SemanticDatabaseService.list: accountId is required');
    }

    const params = new URLSearchParams();

    params.set('account_id', accountId);

    if (includeDeleted) {
      params.set('include_deleted', 'true');
    }

    const query = params.toString();
    const url = `${BASE_URL}${query ? `?${query}` : ''}`;
    return fetchWithAuth(url, { method: 'GET' });

  },

  async getById(accountId, semanticDatabaseId) {
    if (!accountId || !semanticDatabaseId) {
      throw new Error('SemanticDatabaseService.getById: accountId and semanticDatabaseId are required');
    }

    const url = `${BASE_URL}/${encodeURIComponent(accountId)}/${encodeURIComponent(semanticDatabaseId)}`;
    return fetchWithAuth(url, { method: 'GET' });
  },

  async create(payload) {
    if (!payload?.account_id) {
      throw new Error('SemanticDatabaseService.create: account_id is required in payload');
    }

    if (!payload?.connection_id) {
      throw new Error('SemanticDatabaseService.create: connection_id is required in payload');
    }

    if (!payload?.name) {
      throw new Error('SemanticDatabaseService.create: name is required in payload');
    }

    return fetchWithAuth(BASE_URL, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  async update(payload) {
    if (!payload?.semantic_database_id) {
      throw new Error('SemanticDatabaseService.update: semantic_database_id is required in payload');
    }

    return fetchWithAuth(BASE_URL, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  },

  async softDelete(semanticDatabaseId) {
    if (!semanticDatabaseId) {
      throw new Error('SemanticDatabaseService.softDelete: semanticDatabaseId is required');
    }

    const params = new URLSearchParams({
      semantic_database_id: semanticDatabaseId
    });

    const url = `${BASE_URL}/soft?${params.toString()}`;
    return fetchWithAuth(url, {
      method: 'DELETE'
    });
  },

  async getUsersAllowed(semanticDatabaseId) {
    if (!semanticDatabaseId) {
      throw new Error('SemanticDatabaseService.getUsersAllowed: semanticDatabaseId is required');
    }

    const params = new URLSearchParams({
      semantic_database_id: semanticDatabaseId
    });

    return fetchWithAuth(`${BASE_URL}/users?${params.toString()}`, {
      method: 'GET'
    });
  },

  async getGroupsAllowed(semanticDatabaseId) {
    if (!semanticDatabaseId) {
      throw new Error('SemanticDatabaseService.getGroupsAllowed: semanticDatabaseId is required');
    }

    const params = new URLSearchParams({
      semantic_database_id: semanticDatabaseId
    });

    return fetchWithAuth(`${BASE_URL}/groups?${params.toString()}`, {
      method: 'GET'
    });
  },


  async listConnections(userId) {
    if (!userId) {
      throw new Error('SemanticDatabaseService.listConnections: userId is required');
    }

    // Updated path to match backend: /sourceconnections/by-user/{user_id}/connection
    const url = `${SOURCE_CONNECTIONS_REST_BASE}/sourceconnections/by-user/${encodeURIComponent(userId)}/connection`;

    const response = await fetchWithAuth(url, { method: 'GET' });
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.connections)) {
      return response.connections;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    return [];
  }
};

export default SemanticDatabaseService;
